'use client';
import { useTranslations } from 'next-intl';
import { forwardRef, type ReactNode } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineReplace } from '@/app/components/icons/BaseLineReplace';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MediaPromptPreview } from '@/app/components/shared/MediaPromptPreview';
import { HeadlessMediaUploader } from '@/app/components/shared/MediaUploader/HeadlessMediaUploader';
import type { UseMediaUploadOptions } from '@/app/components/shared/MediaUploader/hooks/use-media-upload';
import type { UploadScene } from '@/app/constants';
import { useDragAndDrop } from '@/app/hooks';
import type { ImagePrompt, UploadFile, VideoPrompt } from '@/app/types';
import { cn } from '@/app/utils';

type OperationItem = {
  key: string;
  icon: ReactNode;
  text: string;
  onClick: () => void;
  disabled?: boolean;
};

export interface MediaUploaderWithPreviewProps {
  className?: string;
  loading?: boolean;
  placeholder?: ReactNode | string | null;
  // 控制是否展示辅助性文案（placeholder 与 "上传中..."文案）
  showHelperText?: boolean;
  // 展示的已上传文件（图片或视频）
  media: ImagePrompt | VideoPrompt | null;
  // 操作按钮列表
  operations?: OperationItem[];
  operationIconClassName?: string;
  closeBoxClassName?: string;
  closeIconClassName?: string;
  innerClassName?: string;
  mediaContainerClassName?: string;
  mediaClassName?: string;
  uploaderClassName?: string;
  uploadIcon?: ReactNode;
  idx?: number;
  draggable?: boolean;
  onItemsReorder?: (sourceIdx: number, targetIdx: number) => void;
  deleteTooltip?: string;
  // 上传相关配置
  disabled?: boolean;
  maxCount?: number; // 总数限制（图片+视频总数）
  maxImageCount?: number; // 图片数量限制（选填）
  maxVideoCount?: number; // 视频数量限制（选填）
  maxVideoDuration?: number;
  minVideoDuration?: number;
  scene?: UploadScene;
  acceptImage?: boolean;
  acceptVideo?: boolean;
  captureCoverImage?: boolean;
  uploadCoverImage?: boolean;
  isInitialSelectedImageRange?: boolean;
  getSelectedImage?: (url: string) => Promise<{ blob: Blob; width: number; height: number } | null>;
  onChange?: UseMediaUploadOptions['onChange'];
  mediaType?: 'image' | 'video';
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
}

/**
 * 包含媒体上传、媒体操作和媒体预览功能的业务组件
 * 支持图片和视频的上传和预览
 *
 * 支持配置 draggable 来开启拖拽排序
 * 默认操作包括媒体替换和删除，可以通过 operations 配置其他操作
 *
 * @param {MediaUploaderWithPreviewProps} props
 * @returns {ReactNode} 媒体上传组件
 */
export const MediaUploaderWithPreview = forwardRef<HTMLDivElement, MediaUploaderWithPreviewProps>(
  (
    {
      className,
      loading,
      placeholder,
      showHelperText = true,
      media,
      operations,
      operationIconClassName,
      closeBoxClassName,
      closeIconClassName,
      innerClassName,
      mediaContainerClassName,
      mediaClassName,
      uploaderClassName,
      uploadIcon = <BaseLinePlus className="flex-shrink-0 text-2xl text-system-white" />,
      idx = 0,
      draggable,
      onItemsReorder,
      deleteTooltip,
      disabled,
      maxCount,
      maxImageCount,
      maxVideoCount,
      maxVideoDuration,
      minVideoDuration,
      scene,
      acceptImage = true,
      acceptVideo = true,
      captureCoverImage,
      uploadCoverImage,
      isInitialSelectedImageRange = true,
      getSelectedImage,
      onChange,
      mediaType = 'image',
      allowTempMode = false,
    },
    ref,
  ) => {
    const t = useTranslations();

    const { onDragOver, onDragStart, onDropCapture } = useDragAndDrop({
      onReorder: onItemsReorder,
    });

    const handleRemoveMedia = () => {
      if (!media) return;

      // 触发删除回调
      onChange?.(
        {
          id: idx.toString(),
          file: null,
          status: 'removed',
        } as UploadFile,
        mediaType,
      );
    };

    return (
      <div
        draggable={draggable}
        onDragStart={(e) => draggable && onDragStart(e, idx)}
        onDragOver={draggable ? onDragOver : undefined}
        onDropCapture={(e) => draggable && onDropCapture(e, idx)}
        className={cn(
          'group relative flex h-full w-full max-w-full flex-col items-center justify-center rounded-10 bg-system-black36 text-system-white64',
          draggable && 'cursor-grab',
          className,
        )}
        ref={ref}
      >
        {loading ? (
          <>
            <Loading className="animate-spin text-2xl" />
            {showHelperText && <div className="mt-2 sm:text-xs">{t('create.createForm.upload.uploading')}</div>}
          </>
        ) : media ? (
          <div
            className={cn('flex h-full w-full items-center', mediaContainerClassName)}
            draggable={false}
            onDragStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div
              className="group relative h-full w-full min-w-[40%] flex-1 rounded-10 bg-system-black36"
              draggable={false}
              onDragStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <MediaPromptPreview
                prompt={media}
                className={cn('h-full w-full rounded-10 bg-system-black36 object-contain', mediaClassName)}
              />

              {/* Hover 时显示操作按钮 */}
              <div
                className={cn(
                  'absolute inset-0 flex items-center justify-center gap-4 rounded-10 bg-system-black36 px-4 opacity-0 transition-opacity group-hover:opacity-100 sm:hidden',
                  innerClassName,
                )}
                draggable={false}
                onDragStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <HeadlessMediaUploader
                  acceptVideo={acceptVideo}
                  acceptImage={acceptImage}
                  maxCount={maxCount}
                  maxImageCount={maxImageCount}
                  maxVideoCount={maxVideoCount}
                  maxVideoDuration={maxVideoDuration}
                  minVideoDuration={minVideoDuration}
                  scene={scene}
                  captureCoverImage={captureCoverImage}
                  uploadCoverImage={uploadCoverImage}
                  isInitialSelectedImageRange={isInitialSelectedImageRange}
                  getSelectedImage={getSelectedImage}
                  onChange={onChange}
                  disabled={disabled}
                  allowTempMode={allowTempMode}
                >
                  {({ openFileSelector }) => (
                    <div
                      className={cn('cursor-pointer text-[28px]', operationIconClassName)}
                      onClick={(e) => {
                        e.stopPropagation();
                        openFileSelector();
                      }}
                    >
                      <BaseLineReplace />
                    </div>
                  )}
                </HeadlessMediaUploader>

                {operations?.map((operation) => (
                  <div
                    className={cn(
                      'cursor-pointer text-[28px]',
                      operation.disabled && 'cursor-not-allowed text-system-white24',
                      operationIconClassName,
                    )}
                    key={operation.key}
                    onClick={!operation.disabled ? operation.onClick : undefined}
                  >
                    {operation.icon}
                  </div>
                ))}

                <CustomTooltip content={deleteTooltip} side="top" sideOffset={8} disableHoverableContent={true}>
                  <div
                    className={cn(
                      '-top-2 -right-2 absolute flex size-6 cursor-pointer items-center justify-center rounded-full bg-system-white24 text-system-white backdrop-blur-[2px]',
                      closeBoxClassName,
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemoveMedia();
                    }}
                  >
                    <BaseLineClose className={cn('text-xl', closeIconClassName)} />
                  </div>
                </CustomTooltip>
              </div>
            </div>
          </div>
        ) : (
          <HeadlessMediaUploader
            acceptVideo={acceptVideo}
            acceptImage={acceptImage}
            maxCount={maxCount}
            maxImageCount={maxImageCount}
            maxVideoCount={maxVideoCount}
            maxVideoDuration={maxVideoDuration}
            minVideoDuration={minVideoDuration}
            scene={scene}
            captureCoverImage={captureCoverImage}
            uploadCoverImage={uploadCoverImage}
            isInitialSelectedImageRange={isInitialSelectedImageRange}
            getSelectedImage={getSelectedImage}
            onChange={onChange}
            disabled={disabled}
            allowTempMode={allowTempMode}
          >
            {({ openFileSelector }) => (
              <div
                className={cn(
                  'inline-flex h-full w-full flex-col items-center justify-center rounded-10',
                  disabled ? 'cursor-not-allowed text-system-white24' : 'cursor-pointer hover:bg-system-black64',
                  innerClassName,
                  uploaderClassName,
                )}
                onClick={openFileSelector}
              >
                {uploadIcon}
                {showHelperText && (
                  <div className="mx-2 mt-2 text-center text-xs sm:mt-1.5">
                    {placeholder || t('create.createForm.upload.placeholder')}
                  </div>
                )}
              </div>
            )}
          </HeadlessMediaUploader>
        )}
      </div>
    );
  },
);

MediaUploaderWithPreview.displayName = 'MediaUploaderWithPreview';
